import {
  BfOpenOrderDto,
  DeliveryAddressDto,
  PriceDto,
  ProductBasePrice,
  ProductNetFillQuantity
} from '@eurogast/hub-client-typescript';
import { ProductDeserializer, UnitDefinition } from '@/services/ProductDeserializer';
import { PriceDeserializer } from '@/services/PriceDeserializer';

export abstract class BfOrderDeserializer {
  public static parseBfOpenOrder(order: string): BfOpenOrderDto {
    const parts = order.split('::');
    if (parts.length !== 7) {
      throw Error(`Invalid open bf order format: ${order}`);
    }
    return {
      id: Number.parseFloat(parts[0]),
      comment: parts[1],
      creationTime: new Date(Date.parse(parts[2])),
      customerId: parts[3],
      tenant: parts[4],
      orderAddress: this.getOrderAddress(parts[5]),
      unloadingArea: this.getUnloadingArea(parts[6])
    };
  }

  public static parseBFOrderItems(source: string): BFOrderItem[] {
    return this.parseArray(source).map((str) => this.parseBFOrderItem(str));
  }

  public static parseBFErrorSkus(source: string): string[] {
    return source.split('::');
  }

  private static parseArray(source: string): string[] {
    if (!source || source === '[]') {
      return [];
    } else if (source.startsWith('[') && source.endsWith(']')) {
      return source.slice(1, -1).split(';;');
    }
    throw Error(`Expected array, but got ${source}`);
  }

  private static getOrderAddress(companyLines: string): DeliveryAddressDto {
    if (companyLines === '') {
      return null;
    }

    // leave unused fields empty/null
    return {
      tenant: null,
      externalId: null,
      companyLines: companyLines.split(','),
      street: null,
      city: null,
      postCode: null,
      countryCode: null,
      unloadingAreas: []
    };
  }

  private static getUnloadingArea(area: string): string {
    if (area === '') {
      return null;
    }
    return area;
  }

  private static parseBFOrderItem(source: string): BFOrderItem {
    const parts = source.split('##');
    if (parts.length !== 10) {
      throw Error(`Invalid open bf order item format: ${source}`);
    }
    return {
      name: parts[0],
      sku: parts[1],
      amount: Number.parseFloat(parts[2]),
      unitAbbr: parts[3],
      imagePath: parts[4],
      storageUnit: parts[5] !== '' ? ProductDeserializer.parseUnit(parts[5]) : null,
      netFillQty: parts[6] !== '' ? ProductDeserializer.parseNetFill(parts[6]) : null,
      basePrice: parts[7] !== '' ? ProductDeserializer.parseBasePrice(parts[7]) : null,
      bulkPrices: parts[8] !== '' ? PriceDeserializer.parsePriceList(parts[8]) : null,
      unit: parts[9] !== '' ? ProductDeserializer.parseUnit(parts[9]) : null
    };
  }
}

export interface BFOrderItem {
  name: string;
  sku: string;
  amount: number;
  unitAbbr: string;
  imagePath: string;
  storageUnit: UnitDefinition;
  netFillQty: ProductNetFillQuantity;
  basePrice: ProductBasePrice;
  bulkPrices: PriceDto[];
  unit: UnitDefinition;
}
